/**
 * Artificial Turf / Fake Grass Installation Cost Calculator
 *
 * Estimates the cost to install artificial grass by area, turf grade, install
 * method (DIY vs professional), region, and whether the existing lawn has to
 * come out first. Installed turf pricing varies enormously, so this engine
 * deliberately OUTPUTS RANGES (low/high) rather than false-precision point
 * values. Every number is a ballpark, and the UI hedges it toward getting
 * local quotes.
 *
 * Deterministic and pure: same inputs always return the same result. It reuses
 * the shared plan-engine metadata seam (buildCanonicalToolContext ->
 * attachToolEngineMetadata). This tool is GRASS-LESS, so no grass type is
 * passed into the context; the size input rides the canonical `lawnSize` slot
 * and the metadata simply reflects that grass is unknown.
 */

import { getRegionFromZipCode, toLegacyRegion } from '@/data/regional-climate'
import {
  DIY_REMOVAL_FRACTION,
  REGIONAL_TURF_COSTS,
  TURF_BASE_PREP_COST,
  TURF_DIY_TOOL_COST,
  TURF_GRADE_LABELS,
  TURF_INFILL_COST,
  TURF_MATERIAL_COSTS,
  TURF_PRO_LABOR_COST,
  TURF_REMOVAL_COST,
  type TurfCostRange,
  type TurfGrade,
  type TurfInstallMethod,
} from '@/data/artificial-turf-costs'
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from '@/lib/lawn-plan-engine'

export interface CostRange {
  low: number
  high: number
}

export interface TurfBudgetItem {
  category: string
  description: string
  low: number
  high: number
  /** Share of the total, midpoint-based. Guarded against a zero total. */
  percentage: number
}

export interface ArtificialTurfCostResult extends DeterministicToolResultMetadata {
  area: number // sq ft
  turfGrade: TurfGrade
  installMethod: TurfInstallMethod
  region: string
  zipCode: string
  removeExistingLawn: boolean

  // Cost components (the four named lines add up to totalCost for the selected method)
  materialCost: CostRange
  baseInfillCost: CostRange
  laborCost: CostRange // 0 for DIY
  removalCost: CostRange // 0 when not removing the existing lawn

  // Summary
  totalCost: CostRange
  costPerSqFt: CostRange

  // DIY vs professional comparison (both always computed)
  diyTotal: CostRange
  proTotal: CostRange

  // Recommendations
  costSavingTips: string[]
  budgetBreakdown: TurfBudgetItem[]
}

const ZERO_RANGE: CostRange = { low: 0, high: 0 }

/** Scale a per-sq-ft rate range by a regional multiplier. */
function scaleRate(range: TurfCostRange, multiplier: number): CostRange {
  return { low: range.low * multiplier, high: range.high * multiplier }
}

/** Sum any number of ranges component-wise. */
function addRanges(...ranges: CostRange[]): CostRange {
  return ranges.reduce(
    (sum, r) => ({ low: sum.low + r.low, high: sum.high + r.high }),
    { low: 0, high: 0 },
  )
}

/** Multiply a per-sq-ft range by an area to get a dollar range. */
function multiplyByArea(range: CostRange, area: number): CostRange {
  return { low: range.low * area, high: range.high * area }
}

/** Round a dollar range to whole dollars. */
function roundDollars(range: CostRange): CostRange {
  return { low: Math.round(range.low), high: Math.round(range.high) }
}

/** Round a per-sq-ft range to cents. */
function roundPerSqFt(range: CostRange): CostRange {
  return {
    low: parseFloat(range.low.toFixed(2)),
    high: parseFloat(range.high.toFixed(2)),
  }
}

const midpoint = (range: CostRange): number => (range.low + range.high) / 2

export function calculateArtificialTurfCost(
  area: number | string,
  turfGrade: TurfGrade,
  installMethod: TurfInstallMethod,
  zipCode: string,
  removeExistingLawn: boolean = false,
): ArtificialTurfCostResult {
  // Grass-less tool: no grassType is passed. The area rides the canonical
  // `lawnSize` slot so the shared metadata seam still records size confidence.
  const context = buildCanonicalToolContext({
    toolId: 'cost',
    lawnSize: area,
  })

  const region = getRegionFromZipCode(zipCode)
  if (region === 'Unknown') {
    throw new Error('We could not determine your region from this ZIP code. Try a nearby ZIP.')
  }

  // Fine-grained climate sub-regions (California, Florida, etc.) fall back to
  // their legacy parent, mirroring the lawn-care cost calculator.
  const costs = REGIONAL_TURF_COSTS[region] ?? REGIONAL_TURF_COSTS[toLegacyRegion(region)]
  if (!costs) {
    throw new Error('Regional turf pricing is not available for your area yet. Try a nearby ZIP.')
  }

  // Guard divide-by-zero and junk input: a non-positive or unparseable area
  // yields zero totals rather than NaN/Infinity. The per-sq-ft RATE is still
  // meaningful (it does not depend on area), so it is reported regardless.
  const canonicalArea = context.lawnSizeSqft
  const area_ = typeof canonicalArea === 'number' && Number.isFinite(canonicalArea) && canonicalArea > 0
    ? canonicalArea
    : 0

  const { laborMultiplier, materialMultiplier } = costs

  // Per-sq-ft rate ranges after regional adjustment.
  const materialRate = scaleRate(TURF_MATERIAL_COSTS[turfGrade], materialMultiplier)
  const basePrepRate = scaleRate(TURF_BASE_PREP_COST, materialMultiplier)
  const infillRate = scaleRate(TURF_INFILL_COST, materialMultiplier)
  const baseInfillRate = addRanges(basePrepRate, infillRate)
  const diyToolRate = scaleRate(TURF_DIY_TOOL_COST, materialMultiplier)
  const proLaborRate = scaleRate(TURF_PRO_LABOR_COST, laborMultiplier)

  const proRemovalRate = removeExistingLawn ? scaleRate(TURF_REMOVAL_COST, laborMultiplier) : ZERO_RANGE
  const diyRemovalRate = removeExistingLawn
    ? scaleRate(
        { low: TURF_REMOVAL_COST.low * DIY_REMOVAL_FRACTION, high: TURF_REMOVAL_COST.high * DIY_REMOVAL_FRACTION },
        materialMultiplier,
      )
    : ZERO_RANGE

  // DIY folds the tool/rental amount into base + prep (no labor line); the pro
  // path keeps base separate and carries an install-labor line instead.
  const diyBaseInfillRate = addRanges(baseInfillRate, diyToolRate)
  const proBaseInfillRate = baseInfillRate

  const diyPerSqFtRate = addRanges(materialRate, diyBaseInfillRate, diyRemovalRate)
  const proPerSqFtRate = addRanges(materialRate, proBaseInfillRate, proLaborRate, proRemovalRate)

  const isDiy = installMethod === 'diy'

  // Round every component line FIRST, then define each method's total as the
  // endpoint-wise sum of its rounded lines. This guarantees the displayed
  // breakdown adds up to the total exactly (and the midpoint percentages sum to
  // 100%), instead of an independently rounded aggregate drifting a dollar or
  // two from the sum of the lines a user can see.
  const materialCost = roundDollars(multiplyByArea(materialRate, area_))

  const diyBaseInfillCost = roundDollars(multiplyByArea(diyBaseInfillRate, area_))
  const diyRemovalCost = roundDollars(multiplyByArea(diyRemovalRate, area_))
  const diyTotal = addRanges(materialCost, diyBaseInfillCost, diyRemovalCost)

  const proBaseInfillCost = roundDollars(multiplyByArea(proBaseInfillRate, area_))
  const proLaborCost = roundDollars(multiplyByArea(proLaborRate, area_))
  const proRemovalCost = roundDollars(multiplyByArea(proRemovalRate, area_))
  const proTotal = addRanges(materialCost, proBaseInfillCost, proLaborCost, proRemovalCost)

  // Selected-method breakdown. The four named lines sum to totalCost exactly.
  const baseInfillCost = isDiy ? diyBaseInfillCost : proBaseInfillCost
  const laborCost = isDiy ? { ...ZERO_RANGE } : proLaborCost
  const removalCost = isDiy ? diyRemovalCost : proRemovalCost
  const totalCost = isDiy ? diyTotal : proTotal
  const costPerSqFt = roundPerSqFt(isDiy ? diyPerSqFtRate : proPerSqFtRate)

  // Budget breakdown as dollar ranges + a midpoint share. Guard the share
  // against a zero total (0-area or all-zero-rate edge cases).
  const totalMid = midpoint(totalCost)
  const share = (range: CostRange): number =>
    totalMid > 0 ? parseFloat(((midpoint(range) / totalMid) * 100).toFixed(1)) : 0

  const budgetBreakdown: TurfBudgetItem[] = [
    {
      category: 'Turf material',
      description: `${TURF_GRADE_LABELS[turfGrade]} grade synthetic grass`,
      low: materialCost.low,
      high: materialCost.high,
      percentage: share(materialCost),
    },
    {
      category: 'Base & infill',
      description: isDiy
        ? 'Aggregate base, weed barrier, infill, plus tool and equipment rental'
        : 'Aggregate base, weed barrier, and infill',
      low: baseInfillCost.low,
      high: baseInfillCost.high,
      percentage: share(baseInfillCost),
    },
  ]

  if (laborCost.high > 0) {
    budgetBreakdown.push({
      category: 'Installation labor',
      description: 'Professional grading, compaction, seaming, and infill',
      low: laborCost.low,
      high: laborCost.high,
      percentage: share(laborCost),
    })
  }

  if (removalCost.high > 0) {
    budgetBreakdown.push({
      category: 'Existing lawn removal',
      description: isDiy ? 'Sod-cutter rental and debris disposal' : 'Sod removal, haul-off, and disposal',
      low: removalCost.low,
      high: removalCost.high,
      percentage: share(removalCost),
    })
  }

  // Cost-saving tips. Every tip keeps the "these are estimates, get quotes"
  // framing that the whole tool leans on.
  const costSavingTips: string[] = [
    'Get at least three local quotes. Installed turf pricing swings widely by crew, base depth, and yard access, so treat every number here as a ballpark, not a bid.',
    'Buy turf by the full roll (usually 15 ft wide) and plan your seams to cut waste. Odd-shaped and narrow yards waste more material and push the per-square-foot cost up.',
    'Compare cost per year, not just install price. Premium turf costs more up front but often carries a longer warranty, so the yearly cost can land lower than a cheap roll you replace sooner.',
  ]

  if (isDiy) {
    costSavingTips.push(
      'Doing the demo, base, and install yourself removes the biggest labor line. Budget for a plate compactor and sod-cutter rental plus seaming supplies, and plan for a physically demanding weekend.',
      'Rent tools by the day and batch the whole job into as few days as possible to keep rental costs down.',
    )
  } else {
    costSavingTips.push(
      'Ask each installer to itemize base depth, infill, and labor separately so you can compare quotes line by line instead of one lump sum.',
      'Off-season installs (late fall or winter in mild climates) sometimes get better crew pricing when demand is low.',
    )
  }

  if (removeExistingLawn) {
    costSavingTips.push(
      'Removing and hauling the old lawn adds a real line to the estimate. Ask whether debris can stay on site or be repurposed to trim disposal fees.',
    )
  }

  return attachToolEngineMetadata(
    {
      area: area_,
      turfGrade,
      installMethod,
      region,
      zipCode,
      removeExistingLawn,
      materialCost,
      baseInfillCost,
      laborCost,
      removalCost,
      totalCost,
      costPerSqFt,
      diyTotal,
      proTotal,
      costSavingTips,
      budgetBreakdown,
    },
    context,
  )
}
